/* SPDX-FileCopyrightText: Copyright (c) 2026 NVIDIA CORPORATION & AFFILIATES. All rights reserved. */
/* SPDX-License-Identifier: Apache-2.0 */

:host {
  --color: var(--nve-sys-interaction-color);
  --border-radius: var(--nve-ref-border-radius-xs);
  --font-size: var(--nve-ref-font-size-100);
  --font-weight: var(--nve-ref-font-weight-regular);
  --min-height: var(--nve-ref-size-800);
  --nve-sys-interaction-state-base: var(--background, var(--nve-sys-layer-canvas-background));
  --text-wrap: nowrap;
  width: 100%;
  cursor: default;
}

[internal-host] {
  color: var(--color);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  text-wrap: var(--text-wrap);
  display: flex;
  align-content: center;
  flex-wrap: wrap;
  width: 100%;
}

slot {
  display: flex;
  flex-direction: column;
  width: 100%;
}

slot[name='nodes'] {
  padding-inline-start: var(--nve-ref-size-500);
  display: flex;
  flex-direction: column;
  align-items: start;
}

slot:not(:has-slotted) {
  display: none !important;
}

[part='_node'] {
  display: flex;
  align-items: center;
  width: 100%;
}

.node-title {
  border-radius: var(--border-radius);
  border-inline-start: var(--nve-ref-border-width-md) solid transparent;
  margin-inline-start: var(--nve-ref-space-xxxs);
  padding: var(--nve-ref-size-200) var(--nve-ref-size-100);
  min-height: var(--min-height);
  width: 100%;
  display: block;
}

[part='_node-header'] {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  min-width: fit-content;
}

:host(:focus-visible) [part='_node-header'],
::slotted(a:focus-visible)::after {
  outline: Highlight solid 2px !important;
  outline: 5px auto -webkit-focus-ring-color !important;
}

[name='content'] {
  padding: var(--nve-ref-size-200);
  display: block;
}

[part='_nodes'] {
  width: 100%;
  position: relative;
  display: block;
  padding-inline-start: var(--nve-ref-size-200);
  overflow: hidden;
  transition: height var(--nve-ref-animation-duration-200) cubic-bezier(0.4, 0, 0.2, 1);
}

:host([expanded]) [part='_nodes'] {
  /* fallback for browsers that do not support intrinsic sizing keywords with calc-size() */
  height: auto;

  /* https://developer.chrome.com/docs/css-ui/animate-to-height-auto#animate_to_and_from_intrinsic_sizing_keywords_with_calc-size */
  height: calc-size(max-content, size);
}

[part='_nodes']::before {
  border-inline-start: var(--node-border);
  inset-inline-start: var(--nve-ref-size-150);
  inset-block: 0;
  display: block;
  content: '';
  position: absolute;
}

nve-icon-button {
  --width: var(--nve-ref-size-400);
  outline: 0 !important;
}

nve-icon[name='caret'] {
  transition: transform var(--nve-ref-animation-duration-150) cubic-bezier(0.4, 0, 0.2, 1);
}

nve-checkbox {
  margin-inline-start: var(--nve-ref-space-xxs);
  margin-block-start: var(--nve-ref-space-xs);
  align-self: start;
}

input {
  outline: 0 !important;
}

::slotted(a) {
  text-decoration: none !important;
  color: var(--color) !important;
  outline: 0;
  vertical-align: middle;
}

::slotted(a)::after {
  display: block;
  content: ' ';
  inset: 0;
  position: absolute;
}

:host(:state(selected):state(selectable-single)) .node-title {
  --nve-sys-interaction-state-ratio: var(--nve-sys-interaction-state-ratio-selected);
}

:host(:state(selected):state(selectable-single)) .node-title::after {
  width: var(--nve-ref-border-width-md);
  border-radius: var(--nve-ref-border-radius-full);
  background: var(--nve-sys-accent-secondary-background);
  content: '';
  display: block;
  position: absolute;
  height: 80%;
  inset-block-start: 10%;
  inset-inline-start: calc(var(--nve-ref-size-50) * -1);
}

:host(:state(selectable-single)) .node-title {
  cursor: pointer;
}

:host(:state(selectable-multi)) {
  nve-icon-button {
    --width: var(--nve-ref-size-500);
  }

  slot[name='nodes'] {
    padding-inline-start: var(--nve-ref-size-700);
  }

  [role='group']::before {
    inset-inline-start: var(--nve-ref-size-200);
  }
}

:host(:state(highlighted)) {
  --border-radius: 0;

  .node-title {
    --nve-sys-interaction-state-ratio: var(--nve-sys-interaction-state-ratio-selected);
  }
}

.node-title {
  background-image: linear-gradient(
    color-mix(
        in oklab,
        var(--nve-sys-interaction-state-base) 100%,
        var(--nve-sys-interaction-state-mix) var(--nve-sys-interaction-state-ratio)
      )
      0 0
  ) !important;

  &:hover {
    --nve-sys-interaction-state-ratio: var(--nve-sys-interaction-state-ratio-hover);
  }
}
